import { useContext, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import type {
  CareHomeGrantPermission,
  TeamCareHomeGrant,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import {
  Banner,
  BannerList,
  Button,
  LoadingSpinner,
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

interface CareHomeGrantsPickerProps {
  teamId: string;
}

const permissionLabels: Record<CareHomeGrantPermission, string> = {
  view: "View only",
  "manage-register": "Manage register",
};

/**
 * Which care home registers an MCA team can reach. A register belongs to the home, so
 * a team gets to it by grant rather than by owning a copy — the same construction as
 * the local authority assignments used for payment reviewer teams.
 */
export function CareHomeGrantsPicker({ teamId }: CareHomeGrantsPickerProps) {
  const { managedLocations } = useContext(ManagedLocationsContext);
  const careHomes = managedLocations.filter(
    (location) => location.type === "careHome" && location.status === "active",
  );

  const [grants, setGrants] = useState<TeamCareHomeGrant[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const loadGrants = async () => {
      try {
        const response = await api.teams.getCareHomeGrants(teamId);
        setGrants(response.data.grants);
      } catch (error) {
        console.error("Failed to load care home grants:", error);
        setLoadFailed(true);
      } finally {
        setIsLoaded(true);
      }
    };
    void loadGrants();
  }, [teamId]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.teams.setCareHomeGrants(teamId, grants);
      renderSuccessToast({ message: "Care home access updated" });
    } catch (error) {
      console.error("Failed to save care home grants:", error);
      renderErrorToast({ message: "Failed to update care home access" });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isLoaded) {
    return <LoadingSpinner label="Loading care home access..." />;
  }

  // An empty picker and a failed request look identical, and saving over the former
  // would revoke every grant this team holds. Say so instead.
  if (loadFailed) {
    return (
      <Box sx={{ maxWidth: "600px", mb: 4 }}>
        <Banner
          bannerType={BannerList.ERROR}
          title="Could not load this team's care home access. Reload the page before changing it."
        />
      </Box>
    );
  }

  const selectedHomes = careHomes.filter((home) =>
    grants.some((grant) => grant.careHomeId === home.id),
  );

  return (
    <Box sx={{ maxWidth: "600px", mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Care Home Access
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Select the care homes whose resident registers this team can reach.
        Manage register also allows adding, removing and transferring residents.
      </Typography>

      <Autocomplete
        multiple
        options={careHomes}
        getOptionLabel={(option) => option.name}
        value={selectedHomes}
        onChange={(_event, newValue) => {
          // Keep the permission already chosen for a home; new ones start at view
          // only, so widening access is always a deliberate second step.
          setGrants(
            newValue.map(
              (home) =>
                grants.find((grant) => grant.careHomeId === home.id) ?? {
                  careHomeId: home.id,
                  permission: "view" as const,
                },
            ),
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} label={option.name} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Care Homes"
            placeholder="Search care homes..."
            size="small"
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        sx={{ mb: 2 }}
      />

      {grants.map((grant) => {
        const home = careHomes.find((h) => h.id === grant.careHomeId);
        return (
          <Box
            key={grant.careHomeId}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 1,
            }}
          >
            <Typography variant="body2">
              {home?.name ?? grant.careHomeId}
            </Typography>
            <Select
              size="small"
              value={grant.permission}
              onChange={(event) => {
                const permission = event.target
                  .value as CareHomeGrantPermission;
                setGrants(
                  grants.map((existing) =>
                    existing.careHomeId === grant.careHomeId
                      ? { ...existing, permission }
                      : existing,
                  ),
                );
              }}
              sx={{ minWidth: "12rem" }}
            >
              {Object.entries(permissionLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      })}

      <Button
        disabled={isSaving}
        label={isSaving ? "Saving..." : "Update Access"}
        onClick={() => {
          void handleSave();
        }}
      />
    </Box>
  );
}
